<template>
  <div class="dev-login">
    <div class="dev-login__card">
      <div class="dev-login__head">
        <div class="dev-login__logo">
          <v-icon color="#38bdf8" size="30">
            {{ step === "otp" ? "mdi-cellphone-key" : "mdi-shield-key-outline" }}
          </v-icon>
        </div>
        <h1 class="dev-login__title">Developer Console</h1>
        <p class="dev-login__subtitle">
          <template v-if="step === 'otp'">
            Enter the 6-digit code from Microsoft Authenticator to finish signing in.
          </template>
          <template v-else>
            Restricted area. Enter the developer secret key to continue.
          </template>
        </p>
      </div>

      <transition name="fade">
        <div v-if="api.isError" class="dev-login__alert">
          <v-icon size="18" color="#fca5a5" class="mr-2">mdi-alert-circle-outline</v-icon>
          <span>{{ friendlyError }}</span>
        </div>
      </transition>

      <!-- Step 1: secret key -->
      <template v-if="step === 'secret'">
        <div class="dev-login__field">
          <label class="dev-login__label">Secret Key</label>
          <div class="dev-login__input-wrap">
            <input
              :type="showKey ? 'text' : 'password'"
              class="dev-login__input"
              placeholder="Enter secret key"
              autocomplete="off"
              v-model="form.secretKey"
              @keyup.enter="submitSecret"
            />
            <button class="dev-login__toggle" type="button" @click="showKey = !showKey">
              <v-icon size="18" color="var(--app-text-muted)">
                {{ showKey ? "mdi-eye-outline" : "mdi-eye-off-outline" }}
              </v-icon>
            </button>
          </div>
        </div>

        <button
          class="dev-login__btn"
          :disabled="api.isLoading || !form.secretKey"
          @click="submitSecret"
        >
          <span v-if="!api.isLoading">Continue</span>
          <v-progress-circular v-else indeterminate size="18" width="2" color="white" />
        </button>
      </template>

      <!-- Step 2: TOTP one-time code -->
      <template v-else>
        <div class="dev-login__field">
          <label class="dev-login__label">Authentication Code</label>
          <input
            ref="otpInput"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            maxlength="6"
            class="dev-login__input dev-login__input--otp"
            placeholder="••••••"
            v-model="form.otp"
            @input="onOtpInput"
            @keyup.enter="submitOtp"
          />
        </div>

        <button
          class="dev-login__btn"
          :disabled="api.isLoading || form.otp.length !== 6"
          @click="submitOtp"
        >
          <span v-if="!api.isLoading">Verify &amp; Unlock</span>
          <v-progress-circular v-else indeterminate size="18" width="2" color="white" />
        </button>

        <button class="dev-login__back" type="button" @click="backToSecret">
          <v-icon size="15" color="var(--app-text-muted)" class="mr-1">mdi-arrow-left</v-icon>
          Use a different secret key
        </button>
      </template>

      <p class="dev-login__foot">
        This session is independent of your normal account login.
      </p>
    </div>
  </div>
</template>

<script>
import Api from "@/objects/api";

export default {
  data: () => ({
    step: "secret", // "secret" | "otp"
    form: { secretKey: null, otp: "" },
    challengeToken: null,
    api: new Api(),
    showKey: false,
  }),
  computed: {
    friendlyError() {
      const err = this.api.error;
      if (!err) return "";
      const s = String(err).toLowerCase();
      if (s.includes("network") || s.includes("timeout") || s.includes("econnrefused")) {
        return "Unable to reach the server. Please try again.";
      }
      // On the OTP step the server messages are already user-friendly
      // ("Invalid or expired authentication code.", session expired, etc.).
      if (this.step === "otp") {
        return String(err).length > 120 ? "Verification failed. Please try again." : err;
      }
      if (
        s.includes("invalid") ||
        s.includes("incorrect") ||
        s.includes("unauthorized") ||
        s.includes("forbidden") ||
        s.includes("secret")
      ) {
        return "Invalid secret key.";
      }
      return String(err).length > 120 ? "Login failed. Please try again." : err;
    },
  },
  created() {
    // Dev-auth lives on the HRDC API. The secret is validated server-side and
    // never ships in this bundle. The returned JWT is signed with the shared
    // framework secret, so it is accepted by every service (HRDC + event APIs).
    //
    // Login is two-step when MFA is enabled on the backend:
    //   1. POST /developer/auth/login { secretKey }
    //        -> { mfaRequired: true, challengeToken }   (MFA on)
    //        -> { token, identity, expiresAt }           (MFA off, legacy)
    //   2. POST /developer/auth/verify-mfa { challengeToken, otp }
    //        -> { token, identity, expiresAt }
    // The full object becomes the dev session. A { status, data: {...} }
    // envelope is also accepted.
    this.api.setMethod("POST");
  },
  methods: {
    // Pull the session object out of either a bare body or a {data:{}} envelope,
    // commit it as the dev session and enter the console.
    completeLogin(resp) {
      const session = resp && resp.data && resp.data.token ? resp.data : resp;
      if (!session || !session.token) {
        this.api.isError = true;
        this.api.error = "Invalid response from server.";
        return false;
      }
      this.$store.commit("updateDevAuth", session);
      this.$router.push({ name: "PageDeveloperSettings" }).catch(() => {});
      return true;
    },
    submitSecret() {
      if (!this.form.secretKey || this.api.isLoading) return;
      this.api.setUrl(`${this.$service.hrdc}/v1/en/developer/auth/login`);
      this.api.setParams({ secretKey: this.form.secretKey });
      this.api.setCallbackError(null); // drop any OTP-step error handler

      this.api.setCallbackCompleted((resp) => {
        const body = resp && resp.data && resp.data.token ? resp.data : resp;
        // MFA required -> hold the challenge token and move to the OTP step.
        if (body && body.mfaRequired && body.challengeToken) {
          this.challengeToken = body.challengeToken;
          this.goToOtp();
          return;
        }
        // MFA disabled -> backend returned a usable session directly.
        this.completeLogin(resp);
      });
      this.api.fetch();
    },
    submitOtp() {
      if (this.form.otp.length !== 6 || this.api.isLoading) return;
      this.api.setUrl(`${this.$service.hrdc}/v1/en/developer/auth/verify-mfa`);
      this.api.setParams({
        challengeToken: this.challengeToken,
        otp: this.form.otp,
      });
      this.api.setCallbackCompleted((resp) => {
        this.completeLogin(resp);
      });
      this.api.setCallbackError((e) => {
        // The 5-minute challenge window lapsed — bounce back to step 1 so the
        // user isn't stuck holding a dead challenge token. Message avoids the
        // words the step-1 error mapper rewrites, so it shows through verbatim.
        const s = String(e || "").toLowerCase();
        if (s.includes("session") || s.includes("expired")) {
          this.step = "secret";
          this.form.otp = "";
          this.challengeToken = null;
          this.api.isError = true;
          this.api.error = "Your verification window lapsed. Please log in again.";
        }
      });
      this.api.fetch();
    },
    onOtpInput() {
      // Keep digits only; auto-submit once 6 are entered.
      this.form.otp = String(this.form.otp || "").replace(/\D/g, "").slice(0, 6);
      if (this.form.otp.length === 6 && !this.api.isLoading) {
        this.submitOtp();
      }
    },
    goToOtp() {
      this.step = "otp";
      this.form.otp = "";
      this.api.isError = false;
      this.api.error = null;
      this.$nextTick(() => {
        if (this.$refs.otpInput) this.$refs.otpInput.focus();
      });
    },
    backToSecret() {
      this.step = "secret";
      this.form.otp = "";
      this.challengeToken = null;
      this.api.isError = false;
      this.api.error = null;
    },
  },
};
</script>

<style scoped>
/* >>> auto-dark: dark companions for the light rules below. Colours here are
   token references only (--app-*, defined in src/plugins/theme-dark.css) — do
   NOT paste literal hexes back in, and do not regenerate this block from the
   light rules: that is what made the palette drift across 226 files before.
   Hand-written `.dark-mode` rules later in this file still override these. <<< */

.dark-mode .dev-login__subtitle {
  color: var(--app-text-muted);
}

.dark-mode .dev-login__label {
  color: var(--app-text-faint);
}

.dark-mode .dev-login__input::placeholder {
  color: var(--app-neutral-fg);
}

.dark-mode .dev-login__back {
  color: var(--app-text-muted);
}

.dark-mode .dev-login__back:hover {
  color: var(--app-text-faint);
}

.dark-mode .dev-login__foot {
  color: var(--app-neutral-fg);
}

/* >>> end auto-dark <<< */

.dev-login {
  width: 100%;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background:
    radial-gradient(1200px 600px at 20% -10%, rgba(56, 189, 248, 0.12), transparent 60%),
    radial-gradient(900px 500px at 100% 110%, rgba(99, 102, 241, 0.12), transparent 60%),
    #0b1220;
  font-family: "Lato", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
  padding: 24px;
}

.dev-login__card {
  width: 100%;
  max-width: 400px;
  background: #111a2e;
  border: 1px solid #1e293b;
  border-radius: 18px;
  padding: 36px 32px 28px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
}

.dev-login__head {
  text-align: center;
  margin-bottom: 24px;
}

.dev-login__logo {
  width: 60px;
  height: 60px;
  border-radius: 16px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
}

.dev-login__title {
  font-size: 1.3rem;
  font-weight: 800;
  color: #e2e8f0;
  margin: 0 0 6px;
  letter-spacing: -0.3px;
}

.dev-login__subtitle {
  font-size: 0.82rem;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
}

.dev-login__alert {
  display: flex;
  align-items: center;
  background: rgba(248, 113, 113, 0.1);
  border: 1px solid rgba(248, 113, 113, 0.25);
  color: #fca5a5;
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 0.82rem;
  font-weight: 600;
  margin-bottom: 18px;
}

.dev-login__field {
  margin-bottom: 18px;
}

.dev-login__label {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: #94a3b8;
  margin-bottom: 8px;
}

.dev-login__input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.dev-login__input {
  width: 100%;
  background: #0b1220;
  border: 1px solid #1e293b;
  border-radius: 10px;
  padding: 12px 44px 12px 14px;
  color: #e2e8f0;
  font-size: 0.9rem;
  font-family: "SFMono-Regular", Menlo, Consolas, monospace;
  letter-spacing: 1px;
  outline: none;
  transition: border-color 0.2s ease;
}

.dev-login__input::placeholder {
  color: #475569;
  letter-spacing: normal;
  font-family: "Lato", sans-serif;
}

.dev-login__input:focus {
  border-color: #38bdf8;
}

.dev-login__input--otp {
  padding: 14px;
  text-align: center;
  font-size: 1.5rem;
  letter-spacing: 12px;
  font-weight: 700;
}

.dev-login__input--otp::placeholder {
  letter-spacing: 12px;
  font-family: "SFMono-Regular", Menlo, Consolas, monospace;
}

.dev-login__toggle {
  position: absolute;
  right: 8px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
}

.dev-login__btn {
  width: 100%;
  height: 46px;
  border-radius: 11px;
  background: linear-gradient(135deg, #0ea5e9, #38bdf8);
  color: #fff;
  font-size: 0.9rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.dev-login__btn:hover:not(:disabled) {
  filter: brightness(1.05);
}

.dev-login__btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.dev-login__back {
  width: 100%;
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.78rem;
  font-weight: 600;
  color: #64748b;
  transition: color 0.2s ease;
}

.dev-login__back:hover {
  color: #94a3b8;
}

.dev-login__foot {
  text-align: center;
  font-size: 0.72rem;
  color: #475569;
  margin: 18px 0 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
